import { maxFramerate } from 'src/types/Match';
import { ControlledPromise } from 'src/utils/controlled-promise';
import { attachMediaElement } from 'src/utils/media/view';
import { measureAsync, recordVideoFrame, VideoFpsSource } from 'src/utils/perf';

import MediaStreamPipe from './base';

class MediaStreamPipeManager extends MediaStreamPipe {
  private timerId: ReturnType<typeof setInterval> | null = null;

  public firstFrameRenderedPromise = new ControlledPromise<void>();

  private markFirstFrameRenderedTimerId: ReturnType<typeof setTimeout> | null = null;

  private canvasAnimationId: number | null = null;

  private supportVideoFrameCallback = 'requestVideoFrameCallback' in HTMLVideoElement.prototype;

  private readonly boundHandleChangeVisibility = this.handleChangeVisibility.bind(this);

  private readonly boundHandleResizeSourceTrack = this.handleResizeSourceTrack.bind(this);

  public pipes: MediaStreamPipe[] = [];

  /** 설정하면 render()마다 해당 source로 videoFps를 기록한다. null이면 기록 안 함. */
  public videoFpsSource: VideoFpsSource | null = null;

  public pipe(pipe: MediaStreamPipe) {
    this.pipes.push(pipe);
    return this;
  }

  public unpipe(target: MediaStreamPipe) {
    this.stopRendering();
    this.pipes = this.pipes.filter((pipe) => pipe !== target);
    if (this.inputVideoEl) {
      this.start();
    } else {
      this.dispose();
    }
  }

  /**
   * @param forceCleanup true일 경우 관련된 모든 파이프와 리소스를 정리하고, 기존의 렌더링 결과물을 비움 (default=false)
   */
  public dispose(forceCleanup = false) {
    if (this.mediaStream || this.inputVideoEl) {
      this.stopRendering();
    }
    if (forceCleanup) {
      this.pipes.forEach((pipe) => {
        pipe.dispose();
      });
      this.outputCanvasEls.forEach((canvas) => {
        canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
      });
    }
    this.pipes = [];
    this.inputVideoEl = null;
  }

  private renderCheckBackground() {
    if (document.hidden && !this.timerId) {
      this.timerId = setInterval(this.render.bind(this), 1000 / maxFramerate);
    }

    this.render();
  }

  private handleChangeVisibility() {
    if (!document?.hidden && this.timerId) {
      this.clearLoop();
      this.render();
      return;
    }

    this.renderCheckBackground();
  }

  public stopRendering() {
    this.clearLoop();
    window.removeEventListener('visibilitychange', this.boundHandleChangeVisibility);
    if (this.inputVideoEl) {
      this.inputVideoEl.removeEventListener('resize', this.boundHandleResizeSourceTrack);
    }
    this.pipes.forEach((pipe, i) => {
      pipe.stopRendering();
      if (i !== 0) {
        pipe.dispose();
      }
    });
  }

  protected clearLoop() {
    if (this.canvasAnimationId) {
      if (this.supportVideoFrameCallback) {
        this.inputVideoEl?.cancelVideoFrameCallback(this.canvasAnimationId);
      } else {
        cancelAnimationFrame(this.canvasAnimationId);
      }
      this.canvasAnimationId = null;
    }
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private handleResizeSourceTrack() {
    if (!this.inputVideoEl?.srcObject) {
      return;
    }
    if (this.inputVideoEl.srcObject instanceof MediaStream) {
      const videoTrack = this.inputVideoEl.srcObject.getVideoTracks()?.[0];
      if (videoTrack) {
        const { width = 0, height = 0 } = videoTrack.getSettings();
        this.inputVideoEl.width = width;
        this.inputVideoEl.height = height;
        this.renderVideo();
      }
    }
  }

  public start() {
    if (!this.inputVideoEl) {
      throw new Error('Cannot start MediaStreamPipe. inputVideoEl not set');
    }
    window.addEventListener('visibilitychange', this.boundHandleChangeVisibility);

    if (this.outputCanvasEls.length === 0) {
      this.outputCanvasEls = [document?.createElement('canvas')];
    }

    const outputCanvasEls = this.outputCanvasEls;
    const firstCanvas = outputCanvasEls[0];

    this.updateMediaStream();
    this.inputVideoEl.addEventListener('resize', this.boundHandleResizeSourceTrack);

    this.pipes.reduce((prevVideo, pipe, i) => {
      const isLast = i === this.pipes.length - 1;
      if (isLast) {
        pipe.setEl(prevVideo, outputCanvasEls);
        return prevVideo;
      } else {
        pipe.setEl(prevVideo);
        pipe.updateMediaStream();
        const pipeMediaStream = pipe.mediaStream;
        if (!pipeMediaStream) {
          return prevVideo;
        }
        const nextVideo = document?.createElement('video');
        nextVideo.addEventListener('resize', this.boundHandleResizeSourceTrack);
        attachMediaElement(nextVideo, pipeMediaStream);
        return nextVideo;
      }
    }, this.inputVideoEl);
    this.renderVideo();
    return {
      mediaStream: this.mediaStream,
      width: firstCanvas.width,
      height: firstCanvas.height,
    };
  }

  public renderVideo() {
    this.clearLoop();
    this.pipes.forEach((pipe) => {
      pipe.renderVideo();
    });

    this.renderCheckBackground();
  }

  public async render() {
    const outputCanvasEls = this.outputCanvasEls;
    const inputVideoEl = this.inputVideoEl;
    if (outputCanvasEls.length === 0 || !inputVideoEl) {
      return;
    }

    if (!this.inputVideoEl) {
      this.stopRendering();
      return;
    }

    const renderLabel = this.videoFpsSource
      ? (`pipe.render.${this.videoFpsSource}` as const)
      : ('pipe.render' as const);
    await measureAsync(renderLabel, async () => {
      for (const pipe of this.pipes) {
        await pipe.render.bind(pipe)();
      }
    });
    if (this.videoFpsSource) {
      recordVideoFrame(this.videoFpsSource);
    }

    if (!this.firstFrameRenderedPromise.ended && this.markFirstFrameRenderedTimerId === null) {
      this.markFirstFrameRenderedTimerId = setTimeout(() => {
        /**
         * canvas draw 직후에는 캡쳐 등의 로직이 정상 동작하지 않을 수 있음
         * 약간의 딜레이 이후에 최초 프레임 렌더링되었다고 마킹
         */
        this.firstFrameRenderedPromise.resolve();
        this.markFirstFrameRenderedTimerId = null;
      }, 100);
    }

    if (!this.timerId && this.inputVideoEl) {
      const renderWithThisBind = this.render.bind(this);
      if (this.supportVideoFrameCallback) {
        this.canvasAnimationId = this.inputVideoEl.requestVideoFrameCallback(renderWithThisBind);
      } else {
        this.canvasAnimationId = requestAnimationFrame(renderWithThisBind);
      }
    }
  }
}

export default MediaStreamPipeManager;
